/**
 * Country flag assets (F32).
 *
 * The platform's only flag representation used to be a pair of Unicode
 * regional-indicator characters printed as text. Windows' emoji font has no
 * flag glyphs, so on roughly half of desktop traffic a flag rendered as two
 * letters or an empty box — in the journey selector, the language picker, every
 * route header and every country list.
 *
 * These are static SVG files served by path, exactly like the badge
 * assets next to them: `packages/assets/static/**` is copied to `/assets/**` by
 * both web apps' `viteStaticCopy` config, so a path string is all a consumer
 * needs. No SVGR, no loader, no runtime dependency. Licence and provenance:
 * `packages/assets/static/flags/README.md`.
 *
 * `getFlagAsset` returns `null` for a code we do not have — and that `null` is
 * the point. The helper it replaces answered an unrecognised code with `'🏳️'`,
 * a white flag, which renders perfectly well on every platform. "We do not know
 * this country" therefore looked exactly like a country. Returning `null`
 * pushes the decision to the caller, and `<CountryFlag>` renders a dashed
 * placeholder that is visibly not a flag.
 */

import type { AssetPath } from '../types';

/** Base path for flag assets (matches the `/assets` static-copy destination). */
const FLAGS_BASE = '/assets/flags';

/**
 * Every ISO-3166-1 alpha-2 code with a vendored SVG, plus `EU`.
 *
 * Derived from `COUNTRIES` in `@tonnie/foundation` at vendoring time and kept
 * in step by `npm run qa:flag-assets`, which fails when a country in
 * `COUNTRIES` has no file here. A list and a directory that agree only by
 * convention drift; the gate is what makes them agree.
 */
export const FLAG_ASSET_CODES: readonly string[] = [
  'AD', 'AE', 'AF', 'AG', 'AL', 'AM', 'AO', 'AR', 'AT', 'AU', 'AZ', 'BA',
  'BB', 'BD', 'BE', 'BF', 'BG', 'BH', 'BI', 'BJ', 'BN', 'BO', 'BR', 'BS',
  'BT', 'BW', 'BY', 'BZ', 'CA', 'CD', 'CF', 'CG', 'CH', 'CI', 'CL', 'CM',
  'CN', 'CO', 'CR', 'CU', 'CV', 'CY', 'CZ', 'DE', 'DJ', 'DK', 'DM', 'DO',
  'DZ', 'EC', 'EE', 'EG', 'ER', 'ES', 'ET', 'EU', 'FI', 'FJ', 'FM', 'FR',
  'GA', 'GB', 'GD', 'GE', 'GH', 'GM', 'GN', 'GQ', 'GR', 'GT', 'GW', 'GY',
  'HK', 'HN', 'HR', 'HT', 'HU', 'ID', 'IE', 'IL', 'IN', 'IQ', 'IR', 'IS',
  'IT', 'JM', 'JO', 'JP', 'KE', 'KG', 'KH', 'KI', 'KM', 'KN', 'KP', 'KR',
  'KW', 'KZ', 'LA', 'LB', 'LC', 'LI', 'LK', 'LR', 'LS', 'LT', 'LU', 'LV',
  'LY', 'MA', 'MC', 'MD', 'ME', 'MG', 'MH', 'MK', 'ML', 'MM', 'MN', 'MO',
  'MR', 'MT', 'MU', 'MV', 'MW', 'MX', 'MY', 'MZ', 'NA', 'NE', 'NG', 'NI',
  'NL', 'NO', 'NP', 'NR', 'NZ', 'OM', 'PA', 'PE', 'PG', 'PH', 'PK', 'PL',
  'PS', 'PT', 'PW', 'PY', 'QA', 'RO', 'RS', 'RU', 'RW', 'SA', 'SB', 'SC',
  'SD', 'SE', 'SG', 'SI', 'SK', 'SL', 'SM', 'SN', 'SO', 'SR', 'SS', 'ST',
  'SV', 'SY', 'SZ', 'TD', 'TG', 'TH', 'TJ', 'TL', 'TM', 'TN', 'TO', 'TR',
  'TT', 'TV', 'TW', 'TZ', 'UA', 'UG', 'US', 'UY', 'UZ', 'VA', 'VC', 'VE',
  'VN', 'VU', 'WS', 'YE', 'ZA', 'ZM', 'ZW',
];

const FLAG_ASSET_CODE_SET = new Set(FLAG_ASSET_CODES);

/** True when a vendored SVG exists for this code. Case-insensitive, trimmed. */
export function hasFlagAsset(code: string | null | undefined): boolean {
  if (!code) return false;
  return FLAG_ASSET_CODE_SET.has(code.trim().toUpperCase());
}

/**
 * URL of the flag SVG for an ISO alpha-2 code, or `null` when there is none.
 *
 * `null` covers an empty string, a placeholder like `'?'`, the
 * `UNKNOWN_DESTINATION` sentinel `'UNK'`, and any code outside `COUNTRIES`.
 * Callers must render something that is visibly not a flag — never a glyph.
 */
export function getFlagAssetUrl(code: string | null | undefined): string | null {
  if (!code) return null;
  const normalized = code.trim().toUpperCase();
  return FLAG_ASSET_CODE_SET.has(normalized) ? `${FLAGS_BASE}/${normalized}.svg` : null;
}

/**
 * Flag asset with metadata, or `null` when there is none.
 *
 * `alt` is intentionally EMPTY: a flag next to its own country name is
 * decorative, and a screen reader announcing "Portugal flag Portugal" is worse
 * than silence. Where the flag is the only identifier, the caller passes an
 * accessible name through `<CountryFlag label>` — `@tonnie/assets` and
 * `@tonnie/ui` hold no copy and no `t()`.
 */
export function getFlagAsset(code: string | null | undefined): AssetPath | null {
  const path = getFlagAssetUrl(code);
  if (path === null) return null;
  return { path, alt: '', width: 21, height: 14 };
}
